export const MOVIE_RECENT_DAYS = 30;
export const MOVIE_POPULAR_LIMIT = 18;

const POPULAR_CATEGORY_TARGETS = [
  { name: '2025', min: 8, max: 10, priority: 1 },
  { name: 'Aksiyon', min: 6, max: 8, priority: 2 },
  { name: '2024', min: 4, max: 5, priority: 3 },
  { name: 'Macera', min: 4, max: 5, priority: 3 },
  { name: 'Gerilim', min: 4, max: 5, priority: 3 },
  { name: 'Korku', min: 4, max: 5, priority: 3 },
  { name: 'Yerli Film', min: 4, max: 5, priority: 3 },
  { name: 'Savaş', min: 4, max: 5, priority: 3 },
  { name: 'Gizem', min: 4, max: 5, priority: 3 },
];

const normalizeCategoryName = (value) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\u0131/g, 'i');

const getNormalizedCategoryLabel = (value) => normalizeCategoryName(value)
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

export const getMovieCategoryGroupId = (name, categoryId) => {
  const normalizedName = getNormalizedCategoryLabel(name);
  return normalizedName
    ? `name:${normalizedName}`
    : `id:${String(categoryId || 'movies')}`;
};

export const isSyntheticMovieCategoryName = (name) => {
  const normalizedName = getNormalizedCategoryLabel(name);
  return (normalizedName.includes('bugun') && normalizedName.includes('populer'))
    || normalizedName.includes('yeni eklenen');
};

export const isAdultMovieCategoryName = (name) => {
  const normalizedRawName = normalizeCategoryName(name);
  const normalizedName = getNormalizedCategoryLabel(name);
  const categoryTokens = normalizedName.split(/\s+/).filter(Boolean);
  return categoryTokens.includes('yetiskin')
    || categoryTokens.includes('adult')
    || categoryTokens.includes('xxx')
    || categoryTokens.includes('erotik')
    || /(^|\s)18\s*\+/.test(normalizedRawName);
};

const isAdultMovie = (movie) => isAdultMovieCategoryName(
  movie?.categoryName || movie?.category_name || movie?.genre || ''
);

const positiveNumericValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
};

export const getMovieAddedValue = (movie) => positiveNumericValue(movie?.added);

export const sortMoviesNewestFirst = (movies = []) => movies
  .map((item, serverIndex) => ({ item, serverIndex }))
  .sort((first, second) => (
    getMovieAddedValue(second.item) - getMovieAddedValue(first.item)
    || first.serverIndex - second.serverIndex
  ))
  .map(({ item }) => item);

const getRecentlyAddedRank = (movie) => {
  const rank = movie?.recentlyAddedRank;
  if (rank == null || rank === '') return null;
  const numericRank = Number(rank);
  return Number.isFinite(numericRank) && numericRank >= 0 ? numericRank : null;
};

export const selectRecentlyAddedMovies = (
  movies = [],
  nowSeconds = Math.floor(Date.now() / 1000)
) => {
  const nonAdultMovies = movies.filter((movie) => !isAdultMovie(movie));
  const rankedMovies = nonAdultMovies
    .map((item, serverIndex) => ({ item, serverIndex, rank: getRecentlyAddedRank(item) }))
    .filter(({ rank }) => rank != null)
    .sort((first, second) => first.rank - second.rank || first.serverIndex - second.serverIndex)
    .map(({ item }) => item);

  if (rankedMovies.length > 0) return sortMoviesNewestFirst(rankedMovies);

  const recentThreshold = nowSeconds - (MOVIE_RECENT_DAYS * 24 * 60 * 60);
  return sortMoviesNewestFirst(
    nonAdultMovies.filter((item) => getMovieAddedValue(item) >= recentThreshold)
  );
};

const getMovieRating = (movie) => {
  const numericRating = Number.parseFloat(
    movie?.rating ?? movie?.imdbRating ?? movie?.imdb_rating ?? 0
  );
  return Number.isFinite(numericRating) ? numericRating : 0;
};

const getMovieIdentity = (movie, fallbackIndex = 0) => String(
  movie?.stream_id || movie?.id || `${movie?.name || movie?.title || 'movie'}-${fallbackIndex}`
);

const hashText = (value) => {
  let hash = 2166136261;
  const text = String(value || '');

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

const getDateSeed = (dateKey) => {
  let hash = 0;
  const text = String(dateKey || '');

  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash);
};

const seededRandom = (seed, index = 0) => {
  const value = Math.sin((seed + index) * 12.9898) * 43758.5453;
  return value - Math.floor(value);
};

const getCategoryOrder = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0
    ? numericValue
    : Number.MAX_SAFE_INTEGER;
};

export const sortMovieCategoriesByServerOrder = (categories = []) => categories
  .map((category, serverIndex) => ({ category, serverIndex }))
  .sort((first, second) => (
    getCategoryOrder(first.category.categoryOrder) - getCategoryOrder(second.category.categoryOrder)
    || first.serverIndex - second.serverIndex
  ))
  .map(({ category }) => category);

export const selectDailyPopularMovies = (
  movies = [],
  limit = MOVIE_POPULAR_LIMIT,
  dateKey = new Date().toISOString().slice(0, 10)
) => {
  const nonAdultMovies = movies.filter((movie) => !isAdultMovie(movie));
  const categories = [];
  const categoryIds = new Set();

  nonAdultMovies.forEach((movie, serverIndex) => {
    const categoryId = String(movie?.categoryId || movie?.category_id || 'movies');
    if (categoryIds.has(categoryId)) return;
    categoryIds.add(categoryId);
    categories.push({
      id: categoryId,
      name: movie?.categoryName || movie?.category_name || movie?.genre || '',
      categoryOrder: movie?.categoryOrder,
      serverIndex,
    });
  });

  const orderedCategories = sortMovieCategoriesByServerOrder(categories);
  const seed = getDateSeed(dateKey);
  const selected = [];
  const usedMovieIds = new Set();

  POPULAR_CATEGORY_TARGETS.forEach((target, targetIndex) => {
    const normalizedTarget = normalizeCategoryName(target.name);
    const matchedCategory = orderedCategories.find((category) => {
      const normalizedCategory = normalizeCategoryName(category.name);
      return normalizedCategory.includes(normalizedTarget)
        || normalizedTarget.includes(normalizedCategory);
    });
    if (!matchedCategory) return;

    const seedOffset = target.priority * 1000;
    const candidates = nonAdultMovies
      .map((item, serverIndex) => ({ item, serverIndex, id: getMovieIdentity(item, serverIndex) }))
      .filter(({ item }) => (
        String(item?.categoryId || item?.category_id || 'movies') === matchedCategory.id
        && getMovieRating(item) >= 7
      ))
      .sort((first, second) => (
        seededRandom(seed, hashText(first.id) + seedOffset)
          - seededRandom(seed, hashText(second.id) + seedOffset)
        || first.serverIndex - second.serverIndex
      ));

    const targetCount = Math.floor(
      seededRandom(seed, targetIndex + seedOffset) * (target.max - target.min + 1)
    ) + target.min;

    let addedCount = 0;
    for (const candidate of candidates) {
      if (usedMovieIds.has(candidate.id)) continue;
      usedMovieIds.add(candidate.id);
      selected.push(candidate.item);
      addedCount += 1;
      if (addedCount >= targetCount) break;
    }
  });

  return selected
    .map((item, serverIndex) => ({ item, serverIndex, id: getMovieIdentity(item, serverIndex) }))
    .sort((first, second) => (
      hashText(`${dateKey}:popular:${first.id}`) - hashText(`${dateKey}:popular:${second.id}`)
      || first.serverIndex - second.serverIndex
    ))
    .slice(0, Math.max(0, limit))
    .map(({ item }) => item);
};

export const formatMovieCount = (value) => {
  const numericValue = Number(value);
  return (Number.isFinite(numericValue) ? numericValue : 0).toLocaleString('tr-TR');
};
